import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, RefreshCw, QrCode, Power, Wifi, WifiOff, AlertCircle, CheckCircle2, Smartphone, Cloud, Copy, ShieldCheck } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : "/api";
const META_WEBHOOK_URL = "https://stilocred.com/api/wpp/meta/webhook";

// Segmentos PABX (cada um com o chip base + 4 chips de rodizio para evitar banimento)
const PABX_SEGMENTOS = [
  { base: "pabx_inss", label: "PABX INSS", segmento: "INSS" },
  { base: "pabx_federal", label: "PABX FEDERAL", segmento: "FEDERAL" },
  { base: "pabx_municipal", label: "PABX MUNICIPAL", segmento: "MUNICIPAL" },
  { base: "pabx_estadual", label: "PABX ESTADUAL", segmento: "ESTADUAL" },
  { base: "pabx_clt", label: "PABX CLT", segmento: "CLT" },
  { base: "pabx_exercito", label: "PABX EXÉRCITO", segmento: "EXERCITO" },
  { base: "pabx_aeronautica", label: "PABX AERONÁUTICA", segmento: "AERONAUTICA" },
  { base: "pabx_marinha", label: "PABX MARINHA", segmento: "MARINHA" },
];
// Sessoes pre-definidas (templates): chip base + base_01..base_04 por segmento
const TEMPLATES = [
  ...PABX_SEGMENTOS.flatMap((p) => [
    { id: p.base, label: p.label, tipo: "pabx", segmento: p.segmento },
    ...[1, 2, 3, 4].map((n) => ({ id: `${p.base}_0${n}`, label: `${p.label} #${n}`, tipo: "pabx", segmento: p.segmento })),
  ]),
  { id: "validador", label: "Validador WhatsApp", tipo: "validador" },
];

const STATUS_COLORS = {
  connected: { bg: "bg-emerald-50", border: "border-emerald-300", text: "text-emerald-700", icon: CheckCircle2, label: "Conectado" },
  qr_ready: { bg: "bg-amber-50", border: "border-amber-300", text: "text-amber-700", icon: QrCode, label: "Aguardando QR" },
  connecting: { bg: "bg-blue-50", border: "border-blue-300", text: "text-blue-700", icon: RefreshCw, label: "Conectando..." },
  waiting: { bg: "bg-slate-50", border: "border-slate-300", text: "text-slate-600", icon: WifiOff, label: "Aguardando" },
  disconnected: { bg: "bg-slate-50", border: "border-slate-300", text: "text-slate-600", icon: WifiOff, label: "Desconectado" },
  logged_out: { bg: "bg-red-50", border: "border-red-300", text: "text-red-700", icon: AlertCircle, label: "Deslogado" },
  error: { bg: "bg-red-50", border: "border-red-300", text: "text-red-700", icon: AlertCircle, label: "Erro" },
};

const EMPTY_META = { id: "", label: "", meta_phone_number_id: "", meta_access_token: "", meta_verify_token: "", meta_app_secret: "", meta_waba_id: "", numero: "", meta_graph_version: "v23.0" };

export default function GestaoConexoesWhatsApp() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [qrModal, setQrModal] = useState({ open: false, sessionId: null, qr: null, status: null, number: null });
  const [newSessionModal, setNewSessionModal] = useState(false);
  const [customId, setCustomId] = useState("");
  const [customLabel, setCustomLabel] = useState("");
  const [metaModal, setMetaModal] = useState(false);
  const [metaForm, setMetaForm] = useState(EMPTY_META);

  const carregar = async () => {
    try {
      const res = await axios.get(`${API}/wpp/baileys/sessions`);
      setSessions(res.data || []);
    } catch (e) {
      toast.error("Erro ao carregar sessões: " + (e.response?.data?.detail || e.message));
    }
  };

  useEffect(() => {
    carregar();
    const t = setInterval(carregar, 5000);
    return () => clearInterval(t);
  }, []);

  // Polling do QR quando modal aberto
  useEffect(() => {
    if (!qrModal.open || !qrModal.sessionId) return;
    const tick = async () => {
      try {
        const res = await axios.get(`${API}/wpp/baileys/sessions/${qrModal.sessionId}/qr`);
        setQrModal((m) => ({ ...m, qr: res.data?.qr || null, status: res.data?.status, number: res.data?.number || null }));
        if (res.data?.status === "connected") {
          toast.success(`Sessão ${qrModal.sessionId} conectada!`);
          setTimeout(() => setQrModal({ open: false, sessionId: null, qr: null, status: null, number: null }), 1500);
          carregar();
        }
      } catch (e) {}
    };
    tick();
    const t = setInterval(tick, 2000);
    return () => clearInterval(t);
  }, [qrModal.open, qrModal.sessionId]);

  const handleAdd = async (template) => {
    if (sessions.find((s) => s.id === template.id)) {
      toast.warning(`Sessão "${template.label}" já existe.`);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/wpp/baileys/sessions`, { id: template.id, label: template.label, tipo: template.tipo });
      toast.success(`Sessão "${template.label}" criada!`);
      await carregar();
    } catch (e) {
      toast.error("Erro: " + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  const handleAddCustom = async () => {
    const id = customId.trim().toLowerCase().replace(/[^a-z0-9_]/g, "_");
    if (!id) return toast.error("ID inválido");
    if (sessions.find((s) => s.id === id)) return toast.warning("ID já existe");
    setLoading(true);
    try {
      await axios.post(`${API}/wpp/baileys/sessions`, { id, label: customLabel || id, tipo: "custom" });
      toast.success("Sessão criada!");
      setCustomId(""); setCustomLabel(""); setNewSessionModal(false);
      await carregar();
    } catch (e) {
      toast.error("Erro: " + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateMeta = async () => {
    const id = metaForm.id.trim().toLowerCase().replace(/[^a-z0-9_]/g, "_");
    if (!id) return toast.error("Informe um ID para a instância");
    if (!metaForm.meta_phone_number_id.trim() || !metaForm.meta_access_token.trim() || !metaForm.meta_verify_token.trim()) {
      return toast.error("Phone Number ID, Access Token e Verify Token são obrigatórios");
    }
    setLoading(true);
    try {
      await axios.post(`${API}/wpp/meta/sessions`, { ...metaForm, id });
      toast.success("Instância Meta (API Oficial) salva!");
      setMetaModal(false);
      setMetaForm(EMPTY_META);
      await carregar();
    } catch (e) {
      toast.error("Erro: " + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async (id) => {
    setLoading(true);
    try {
      await axios.post(`${API}/wpp/baileys/sessions/${id}/connect`);
      toast.info("Iniciando conexão... abra o QR Code para escanear.");
      setQrModal({ open: true, sessionId: id, qr: null, status: "connecting", number: null });
    } catch (e) {
      toast.error("Erro: " + (e.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  };

  const handleDisconnect = async (id) => {
    if (!window.confirm(`Desconectar a sessão "${id}"? Os dados de auth serão removidos.`)) return;
    setLoading(true);
    try {
      await axios.post(`${API}/wpp/baileys/sessions/${id}/disconnect`);
      toast.success("Desconectado");
      await carregar();
    } catch (e) {
      toast.error("Erro: " + (e.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  };

  const handleDelete = async (session) => {
    const id = typeof session === "string" ? session : session.id;
    const isMeta = typeof session === "object" && session.tipo_conexao === "meta";
    if (id === "central") return toast.error('Sessão "central" não pode ser deletada.');
    if (!window.confirm(`DELETAR a instância "${id}" permanentemente?`)) return;
    setLoading(true);
    try {
      if (isMeta) {
        await axios.delete(`${API}/wpp/meta/sessions/${id}`);
      } else {
        await axios.delete(`${API}/wpp/baileys/sessions/${id}`);
      }
      toast.success("Removida");
      await carregar();
    } catch (e) {
      toast.error("Erro: " + (e.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  };

  const handleShowQr = (id) => {
    setQrModal({ open: true, sessionId: id, qr: null, status: null, number: null });
  };

  const copyWebhook = () => {
    navigator.clipboard.writeText(META_WEBHOOK_URL);
    toast.success("URL do Webhook copiada!");
  };

  return (
    <div className="space-y-6 p-6" data-testid="gestao-conexoes-wpp">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Gestão Conexões WhatsApp</h1>
          <p className="text-sm text-slate-600 mt-1">
            Escolha entre <strong>Baileys (QR)</strong> ou <strong>Meta (API Oficial)</strong> para cada número. O sistema carrega o driver correto automaticamente.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={carregar} data-testid="btn-refresh-sessions">
            <RefreshCw className="h-4 w-4 mr-1" /> Atualizar
          </Button>
          <Button variant="outline" size="sm" onClick={() => setNewSessionModal(true)} data-testid="btn-novo-id-custom">
            <Plus className="h-4 w-4 mr-1" /> Baileys (ID Custom)
          </Button>
          <Button size="sm" onClick={() => { setMetaForm(EMPTY_META); setMetaModal(true); }} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-nova-meta">
            <Cloud className="h-4 w-4 mr-1" /> Nova Instância Meta (API)
          </Button>
        </div>
      </div>

      {/* Sessoes existentes */}
      <div>
        <h2 className="text-sm font-semibold text-slate-700 mb-3 uppercase tracking-wide">Instâncias Ativas ({sessions.length})</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sessions.map((s) => {
            const isMeta = s.tipo_conexao === "meta";
            const cfg = STATUS_COLORS[s.status] || STATUS_COLORS.disconnected;
            const Icon = cfg.icon;
            return (
              <Card key={s.id} className={`${isMeta ? "bg-blue-50 border-blue-300" : `${cfg.bg} ${cfg.border}`} border-2`} data-testid={`session-card-${s.id}`}>
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center justify-between text-base">
                    <span className="flex items-center gap-2">
                      {isMeta ? <Cloud className="h-5 w-5 text-blue-600" /> : <Smartphone className="h-5 w-5" />} {s.label}
                    </span>
                    {isMeta ? (
                      <Badge variant="outline" className="text-blue-700 text-xs border-blue-400">
                        <ShieldCheck className="h-3 w-3 mr-1" /> Meta API Oficial
                      </Badge>
                    ) : (
                      <Badge variant="outline" className={`${cfg.text} text-xs`}>
                        <Icon className="h-3 w-3 mr-1" /> {cfg.label}
                      </Badge>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="text-xs text-slate-600">
                    <div><strong>ID:</strong> <code>{s.id}</code></div>
                    <div><strong>Driver:</strong> {isMeta ? "Meta Cloud API" : "Baileys (QR)"}</div>
                    {s.number && <div><strong>Número:</strong> {String(s.number).replace(/@.*/, "")}</div>}
                    {s.error && <div className="text-red-600 mt-1"><strong>Erro:</strong> {s.error}</div>}
                  </div>
                  <div className="flex gap-1 flex-wrap pt-2 border-t">
                    {isMeta ? (
                      <span className="text-xs text-blue-700 flex items-center gap-1 py-1"><CheckCircle2 className="h-3 w-3" /> Conectada via API (webhook)</span>
                    ) : s.status === "connected" ? (
                      <Button variant="outline" size="sm" onClick={() => handleDisconnect(s.id)} className="text-red-600 border-red-300" data-testid={`btn-disconnect-${s.id}`}>
                        <Power className="h-3 w-3 mr-1" /> Desconectar
                      </Button>
                    ) : (
                      <>
                        <Button variant="outline" size="sm" onClick={() => handleConnect(s.id)} className="text-emerald-600 border-emerald-300" data-testid={`btn-connect-${s.id}`}>
                          <Wifi className="h-3 w-3 mr-1" /> Conectar
                        </Button>
                        {s.status === "qr_ready" && (
                          <Button variant="outline" size="sm" onClick={() => handleShowQr(s.id)} className="text-amber-700 border-amber-300" data-testid={`btn-qr-${s.id}`}>
                            <QrCode className="h-3 w-3 mr-1" /> Ver QR
                          </Button>
                        )}
                      </>
                    )}
                    {s.id !== "central" && (
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(s)} className="text-red-700" data-testid={`btn-delete-${s.id}`}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Templates para adicionar */}
      <div>
        <h2 className="text-sm font-semibold text-slate-700 mb-3 uppercase tracking-wide">Adicionar Sessão Baileys (Templates)</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
          {TEMPLATES.filter((t) => !sessions.find((s) => s.id === t.id)).map((t) => (
            <Button key={t.id} variant="outline" size="sm" onClick={() => handleAdd(t)} disabled={loading} className="justify-start text-xs" data-testid={`btn-add-${t.id}`}>
              <Plus className="h-3 w-3 mr-1" /> {t.label}
            </Button>
          ))}
          {TEMPLATES.every((t) => sessions.find((s) => s.id === t.id)) && (
            <p className="text-xs text-slate-500 col-span-full italic">Todas as sessões pré-definidas já foram criadas.</p>
          )}
        </div>
      </div>

      {/* Modal QR */}
      <Dialog open={qrModal.open} onOpenChange={(o) => !o && setQrModal({ open: false, sessionId: null, qr: null, status: null, number: null })}>
        <DialogContent className="max-w-md" data-testid="qr-modal">
          <DialogHeader>
            <DialogTitle>QR Code - {qrModal.sessionId}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center justify-center py-4 min-h-[320px]">
            {qrModal.status === "connected" ? (
              <div className="text-center">
                <CheckCircle2 className="h-16 w-16 text-emerald-500 mx-auto mb-2" />
                <p className="text-emerald-700 font-semibold">Conectado!</p>
                {qrModal.number && <p className="text-sm text-slate-600 mt-1">{qrModal.number.replace(/@.*/, "")}</p>}
              </div>
            ) : qrModal.qr ? (
              <>
                <img src={qrModal.qr} alt="QR Code" className="w-72 h-72" />
                <p className="text-sm text-slate-600 mt-3 text-center">
                  Abra o WhatsApp no celular → Configurações → Aparelhos conectados → Conectar um aparelho
                </p>
              </>
            ) : (
              <div className="text-center text-slate-500">
                <RefreshCw className="h-8 w-8 mx-auto animate-spin mb-2" />
                <p>Aguardando QR Code...</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setQrModal({ open: false, sessionId: null, qr: null, status: null, number: null })} data-testid="btn-close-qr">Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal ID custom (Baileys) */}
      <Dialog open={newSessionModal} onOpenChange={setNewSessionModal}>
        <DialogContent data-testid="modal-novo-id-custom">
          <DialogHeader>
            <DialogTitle>Criar Sessão Baileys (ID Customizado)</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>ID (apenas a-z, 0-9, _)</Label>
              <Input value={customId} onChange={(e) => setCustomId(e.target.value)} placeholder="ex: marketing, suporte_2" data-testid="input-custom-id" />
            </div>
            <div>
              <Label>Nome amigável</Label>
              <Input value={customLabel} onChange={(e) => setCustomLabel(e.target.value)} placeholder="ex: Suporte Pós-Venda" data-testid="input-custom-label" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewSessionModal(false)}>Cancelar</Button>
            <Button onClick={handleAddCustom} disabled={!customId || loading} data-testid="btn-criar-custom">Criar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Meta (API Oficial) */}
      <Dialog open={metaModal} onOpenChange={setMetaModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="modal-nova-meta">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Cloud className="h-5 w-5 text-blue-600" /> Nova Instância Meta (API Oficial)</DialogTitle>
            <DialogDescription>
              Configure as credenciais do WhatsApp Business (Meta Cloud API). Obtenha-as em <strong>developers.facebook.com</strong> → seu App → WhatsApp → Configuração da API.
            </DialogDescription>
          </DialogHeader>

          <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-xs space-y-2">
            <div className="font-semibold text-blue-800">1) Configure este Webhook no painel do Facebook Developers:</div>
            <div className="flex items-center gap-2">
              <code className="bg-white px-2 py-1 rounded border border-blue-200 text-[11px] break-all flex-1" data-testid="meta-webhook-url">{META_WEBHOOK_URL}</code>
              <Button type="button" variant="outline" size="sm" onClick={copyWebhook} data-testid="btn-copy-webhook"><Copy className="h-3 w-3" /></Button>
            </div>
            <div className="text-blue-700">Use o mesmo <strong>Verify Token</strong> abaixo no campo "Token de Verificação" da Meta e assine o campo <strong>messages</strong>.</div>
          </div>

          <div className="space-y-3 py-1">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>ID da Instância *</Label>
                <Input value={metaForm.id} onChange={(e) => setMetaForm({ ...metaForm, id: e.target.value })} placeholder="ex: meta_principal" data-testid="input-meta-id" />
              </div>
              <div>
                <Label>Nome amigável</Label>
                <Input value={metaForm.label} onChange={(e) => setMetaForm({ ...metaForm, label: e.target.value })} placeholder="ex: WhatsApp Oficial" data-testid="input-meta-label" />
              </div>
            </div>
            <div>
              <Label>Phone Number ID *</Label>
              <Input value={metaForm.meta_phone_number_id} onChange={(e) => setMetaForm({ ...metaForm, meta_phone_number_id: e.target.value })} placeholder="ex: 123456789012345" data-testid="input-meta-phoneid" />
            </div>
            <div>
              <Label>Access Token *</Label>
              <Input value={metaForm.meta_access_token} onChange={(e) => setMetaForm({ ...metaForm, meta_access_token: e.target.value })} placeholder="EAAG..." data-testid="input-meta-token" />
            </div>
            <div>
              <Label>Verify Token * (você define)</Label>
              <Input value={metaForm.meta_verify_token} onChange={(e) => setMetaForm({ ...metaForm, meta_verify_token: e.target.value })} placeholder="um valor secreto qualquer" data-testid="input-meta-verify" />
            </div>
            <div>
              <Label>App Secret (recomendado)</Label>
              <Input value={metaForm.meta_app_secret} onChange={(e) => setMetaForm({ ...metaForm, meta_app_secret: e.target.value })} placeholder="valida a assinatura X-Hub-Signature-256" data-testid="input-meta-secret" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-1">
                <Label>WABA ID</Label>
                <Input value={metaForm.meta_waba_id} onChange={(e) => setMetaForm({ ...metaForm, meta_waba_id: e.target.value })} data-testid="input-meta-waba" />
              </div>
              <div className="col-span-1">
                <Label>Número (exibição)</Label>
                <Input value={metaForm.numero} onChange={(e) => setMetaForm({ ...metaForm, numero: e.target.value })} placeholder="5511..." data-testid="input-meta-numero" />
              </div>
              <div className="col-span-1">
                <Label>Graph Version</Label>
                <Input value={metaForm.meta_graph_version} onChange={(e) => setMetaForm({ ...metaForm, meta_graph_version: e.target.value })} data-testid="input-meta-graph" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMetaModal(false)}>Cancelar</Button>
            <Button onClick={handleCreateMeta} disabled={loading} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-salvar-meta">Salvar Instância</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
